/**
 * @spec docs/specs/features/staking-ui-batched-reads.md (§1, R8)
 *
 * When a served value may be shown, and when it may back an amount-dependent action.
 *
 * A value's age is `(servedAt − observedAt) + (now − receivedAt)`: the first term is server clock
 * only, the second client clock only, so a skewed client clock cannot make a value look fresh.
 * Past five minutes a value is unavailable ("—"). Before that it is shown, marked stale when the
 * server served it stale, the last refresh failed, it is below the client's minBlock floor, or an
 * uncached read of it is still owed (a pending blockless refresh);
 * only a non-stale value may enable an action.
 */
import type { Observation } from './read-api';

export const MAX_VALUE_AGE_MS = 5 * 60_000;

export interface Reading {
  readonly value: bigint;
  readonly stale: boolean;
  /** Client time at which the value stops being available. */
  readonly expiresAt: number;
}

export interface ReadingContext {
  /** Client time the response was received. */
  readonly receivedAt: number;
  readonly now: number;
  /** Lowest acceptable block for this value, or null. */
  readonly floor: bigint | null;
  /** The latest refresh of this value failed. */
  readonly refreshFailed: boolean;
  /** A forced (bypassCache) refresh of this value has not delivered yet. */
  readonly refreshPending?: boolean;
}

export function readObservation(obs: Observation | undefined, ctx: ReadingContext): Reading | null {
  if (!obs || obs.value === null || obs.ageAtServeMs === null) return null;
  const expiresAt = ctx.receivedAt + MAX_VALUE_AGE_MS - obs.ageAtServeMs;
  if (ctx.now >= expiresAt) return null;
  const belowFloor =
    ctx.floor !== null && (obs.blockNumber === null || obs.blockNumber < ctx.floor);
  return {
    value: obs.value,
    stale: obs.stale || ctx.refreshFailed || belowFloor || ctx.refreshPending === true,
    expiresAt,
  };
}
